import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Send, 
  MessageSquare, 
  User, 
  CheckCircle2,
  Award,
  Calendar
} from 'lucide-react';
import { sendContactEmail } from '../utils/emailjs';
import Toast from '../components/shared/Toast';

const Contact = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  
  const initialFormData = {
    name: '',
    email: '',
    phone: '',
    subject: searchParams.get('subject') === 'audit' ? 'audit' : '',
    message: searchParams.get('message') || '',
    preferredDate: ''
  };

  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Format d\'email invalide';
    }
    
    if (!formData.subject) {
      newErrors.subject = 'Le sujet est requis';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Le message est requis';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await sendContactEmail(formData);
      setIsSubmitting(false);
      setIsSubmitted(true);
      setToastMessage('Votre message a été envoyé avec succès !');
      setToastType('success');
      setShowToast(true);
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      setIsSubmitting(false);
      setToastMessage('Une erreur est survenue lors de l\'envoi. Veuillez réessayer.');
      setToastType('error');
      setShowToast(true);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="pt-32 pb-16 px-4 bg-gradient-to-br from-white to-gray-100"
    >
      <Toast
        show={showToast}
        message={toastMessage}
        type={toastType}
        onClose={() => setShowToast(false)}
      />

      <div className="container mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">Contactez-nous</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Notre équipe est à votre disposition pour répondre à toutes vos questions concernant 
            les diagnostics immobiliers.
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          {/* Informations de contact */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="lg:sticky lg:top-32"
          >
            <div className="bg-gradient-to-br from-dk-blue to-dk-light-blue text-white rounded-lg shadow-lg p-8">
              <motion.div variants={itemVariants} className="flex items-center space-x-2 mb-8">
                <Award className="h-6 w-6" />
                <span className="text-xl font-bold">Diagnosti-K</span>
              </motion.div>
              
              <motion.ul variants={containerVariants} className="space-y-6">
                <motion.li variants={itemVariants} className="flex items-start space-x-4">
                  <Phone className="h-6 w-6 mt-1" />
                  <div>
                    <p className="font-medium">Téléphone</p>
                    <a href="tel:0789307500" className="text-lg hover:text-white/80 transition-colors">
                      07 89 30 75 00
                    </a>
                    <p className="text-sm text-white/80">Lun-Ven: 8h-19h</p>
                  </div>
                </motion.li>
                
                <motion.li variants={itemVariants} className="flex items-start space-x-4">
                  <Mail className="h-6 w-6 mt-1" />
                  <div>
                    <p className="font-medium">Email</p>
                    <a href="mailto:Spskdiag@gmail.com" className="text-lg hover:text-white/80 transition-colors">
                      Spskdiag@gmail.com
                    </a>
                    <p className="text-sm text-white/80">Réponse sous 24-48h</p>
                  </div>
                </motion.li>
                
                <motion.li variants={itemVariants} className="flex items-start space-x-4">
                  <MapPin className="h-6 w-6 mt-1" />
                  <div>
                    <p className="font-medium">Adresse</p>
                    <p className="text-lg">95 avenue Fernand Gonnet</p>
                    <p>84850 Camaret-sur-Aigues</p>
                  </div>
                </motion.li>
                
                <motion.li variants={itemVariants} className="flex items-start space-x-4">
                  <Clock className="h-6 w-6 mt-1" />
                  <div>
                    <p className="font-medium">Horaires</p>
                    <p>Lun-Ven: 8h-19h</p>
                    <p className="text-sm text-white/80">Sam: Sur rendez-vous</p>
                  </div>
                </motion.li>
              </motion.ul>
            </div>
          </motion.div>
          
          {/* Formulaire de contact */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="lg:col-span-2"
          >
            {isSubmitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-lg shadow-lg p-8 text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Message envoyé avec succès !</h3>
                <p className="text-lg mb-6 text-gray-600">
                  Merci pour votre message. Notre équipe vous contactera dans les plus brefs délais.
                </p>
              </motion.div>
            ) : (
              <div className="bg-white rounded-lg shadow-lg p-8">
                <motion.h3 variants={itemVariants} className="text-2xl font-bold mb-6 text-gray-900">
                  Envoyez-nous un message
                </motion.h3>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <motion.div variants={itemVariants}>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Nom complet *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <User className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-dk-blue focus:border-transparent ${
                            errors.name ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Votre nom et prénom"
                        />
                      </div>
                      {errors.name && (
                        <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                      )}
                    </motion.div>
                    
                    <motion.div variants={itemVariants}>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Mail className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-dk-blue focus:border-transparent ${
                            errors.email ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="votre.email@exemple.com"
                        />
                      </div>
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                      )}
                    </motion.div>
                    
                    <motion.div variants={itemVariants}>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Téléphone (optionnel)
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Phone className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-dk-blue focus:border-transparent"
                          placeholder="07 XX XX XX XX"
                        />
                      </div>
                    </motion.div>
                    
                    <motion.div variants={itemVariants}>
                      <label htmlFor="preferredDate" className="block text-sm font-medium text-gray-700 mb-1">
                        Date souhaitée (optionnel)
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Calendar className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="date"
                          id="preferredDate"
                          name="preferredDate"
                          value={formData.preferredDate}
                          onChange={handleChange}
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-dk-blue focus:border-transparent"
                        />
                      </div>
                    </motion.div>
                  </div>

                  <motion.div variants={itemVariants}>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                      Type de diagnostic *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MessageSquare className="h-5 w-5 text-gray-400" />
                      </div>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-dk-blue focus:border-transparent appearance-none ${
                          errors.subject ? 'border-red-500' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Sélectionnez un diagnostic</option>
                        <option value="audit">Audit énergétique</option>
                        <option value="dpe">DPE / Audit énergétique</option>
                        <option value="amiante">Amiante</option>
                        <option value="electricite">Électricité</option>
                        <option value="gaz">Gaz</option>
                        <option value="termites">Termites</option>
                        <option value="plomb">Plomb</option>
                        <option value="carrez">Mesurage Carrez</option>
                        <option value="erp">État des Risques (ERP)</option>
                        <option value="autre">Autre demande</option>
                      </select>
                    </div>
                    {errors.subject && (
                      <p className="mt-1 text-sm text-red-600">{errors.subject}</p>
                    )}
                  </motion.div>
                  
                  <motion.div variants={itemVariants}>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-dk-blue focus:border-transparent ${
                        errors.message ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Décrivez votre besoin en diagnostic immobilier..."
                    ></textarea>
                    {errors.message && (
                      <p className="mt-1 text-sm text-red-600">{errors.message}</p>
                    )}
                  </motion.div>
                  
                  <motion.div variants={itemVariants} className="flex justify-end">
                    <button
                      type="submit"
                      className="btn-primary flex items-center"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          Envoyer le message
                          <Send className="h-4 w-4 ml-2" />
                        </>
                      )}
                    </button>
                  </motion.div>
                </form>
              </div>
            )}

            {/* Map */}
            <motion.div
              variants={itemVariants}
              className="mt-8 bg-white rounded-lg shadow-lg overflow-hidden"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d45744.320811354974!2d4.778380591723633!3d44.13850999999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12b57d27e1052b23%3A0x408ab2ae4c1f000!2s84100%20Orange!5e0!3m2!1sfr!2sfr!4v1711462552614!5m2!1sfr!2sfr"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="rounded-lg"
              ></iframe>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default Contact;